"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { getRandomBackgroundImage } from "@/hooks/background-images/backgroundImagesApi";
import { isAdminPath } from "@/lib/routeAccess";

/**
 * The dashboard's full-screen backdrop.
 *
 * Member pages get a rotating nature photo. Admin pages get plain black
 * (TX-5169): they are dense tables and forms, and a photo behind them made
 * them harder to read. This is the one place that decides it — it is mounted
 * by `DashboardShell`, above every admin route — and it matches on the
 * `/admin` prefix, so an admin page added later is black without opting in.
 *
 * On admin pages the photo is not only hidden but never requested.
 */
export const RotatingBackground: React.FC = () => {
  const $ = _c(8);
  const t0 = usePathname() ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = isAdminPath(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isAdmin = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["backgroundImage"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !isAdmin;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: getRandomBackgroundImage,
      enabled: t3,
      refetchInterval: 300000,
      staleTime: 300000,
      gcTime: 300000,
      refetchOnWindowFocus: false
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data
  } = useQuery(t4);
  let t5;
  if ($[5] !== data || $[6] !== isAdmin) {
    t5 = <div className="z-[-1] fixed inset-0 overflow-hidden" data-dashboard-background={true}>{isAdmin ? <div className="absolute inset-0 bg-admin-background" data-admin-background={true} /> : !data?.url ? <div className="absolute inset-0 bg-primary-900/80" /> : <Image src={data.url} alt="" fill={true} priority={true} quality={90} sizes="100vw" className="object-cover transition-opacity duration-1000 ease-in-out" style={{
        opacity: 1
      }} />}</div>;
    $[5] = data;
    $[6] = isAdmin;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};